import React, { useEffect, useMemo, useState } from 'react'
import { Calendar, CalendarX, Clock, GraduationCap, Loader2 } from 'lucide-react'
import { apiClient } from '@/utils/api'

/**
 * Read-only "Upcoming School Events" section on `/parent/school`.
 *
 * Uses the pre-existing `/api/school/events` endpoint (HP Phoenix) which
 * aggregates subscribed-school platform events + family custom events +
 * local school/district calendar events. Matches the data sources behind
 * the kiosk's no-school awareness and the transport preview annotations.
 *
 * Deliberately read-only — editing events was the Go-era UI we stripped in
 * v6.0.70. Create/delete flows come back in a later change if needed.
 */

type EventEntry = {
  id?: string
  date: string
  name?: string
  title?: string
  event_type?: string
  school?: string
  school_name?: string
  district?: string
  district_name?: string
  all_day?: boolean
  start_time?: string | null
  end_time?: string | null
  no_school?: boolean
  early_release?: boolean
  affects_transportation?: boolean
  description?: string | null
  affected_kids?: string[]
}

const DEFAULT_DAYS_AHEAD = 60

function formatDateHeader(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatTime(t?: string | null): string | null {
  if (!t) return null
  const [h, m] = t.split(':')
  const hh = parseInt(h, 10)
  const ampm = hh >= 12 ? 'PM' : 'AM'
  const hr = hh > 12 ? hh - 12 : (hh === 0 ? 12 : hh)
  return `${hr}:${m} ${ampm}`
}

function eventVariant(e: EventEntry): 'no_school' | 'early_release' | 'transportation' | 'default' {
  if (e.no_school) return 'no_school'
  if (e.early_release) return 'early_release'
  if (e.affects_transportation) return 'transportation'
  return 'default'
}

function IconForVariant({ v }: { v: ReturnType<typeof eventVariant> }) {
  switch (v) {
    case 'no_school': return <CalendarX className="w-4 h-4 text-red-600" />
    case 'early_release': return <Clock className="w-4 h-4 text-amber-600" />
    case 'transportation': return <GraduationCap className="w-4 h-4 text-sky-600" />
    default: return <Calendar className="w-4 h-4 text-gray-500" />
  }
}

const UpcomingSchoolEvents: React.FC = () => {
  const [events, setEvents] = useState<EventEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const resp: any = await apiClient.get(`/api/school/events?days_ahead=${DEFAULT_DAYS_AHEAD}`)
        if (cancelled) return
        // Endpoint returns a raw array.
        const list: EventEntry[] = Array.isArray(resp) ? resp : (resp?.events || [])
        setEvents(list)
      } catch (e: any) {
        if (cancelled) return
        setError(e?.response?.data?.error || e?.message || 'Failed to load events')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const groupedByDate = useMemo(() => {
    const byDate: Record<string, EventEntry[]> = {}
    events.forEach(e => {
      if (!e.date) return
      if (!byDate[e.date]) byDate[e.date] = []
      byDate[e.date].push(e)
    })
    return Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b))
  }, [events])

  return (
    <div className="bg-white rounded-lg shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-semibold text-gray-900">Upcoming School Events</h3>
        <span className="text-xs text-gray-500">Next {DEFAULT_DAYS_AHEAD} days</span>
      </div>

      {error && <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded">{error}</div>}
      {loading && <div className="text-gray-500 text-sm flex items-center"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading…</div>}

      {!loading && groupedByDate.length === 0 && !error && (
        <div className="text-gray-500 text-sm">
          No upcoming events in the next {DEFAULT_DAYS_AHEAD} days. Events appear here once your subscribed schools and districts publish them.
        </div>
      )}

      {groupedByDate.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {groupedByDate.map(([date, list]) => (
            <li key={date} className="py-2 flex gap-4">
              <div className="w-28 shrink-0 text-sm font-medium text-gray-700">{formatDateHeader(date)}</div>
              <ul className="flex-1 space-y-1">
                {list.map((e, i) => {
                  const variant = eventVariant(e)
                  const name = e.name || e.title || '(untitled event)'
                  const source = e.school || e.school_name || e.district_name || e.district || ''
                  const time = e.all_day ? 'All day' : (formatTime(e.start_time) || null)
                  return (
                    <li key={e.id || `${date}-${i}`} className="flex items-start gap-2 text-sm">
                      <IconForVariant v={variant} />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="font-medium text-gray-900">{name}</span>
                          {variant === 'no_school' && <span className="text-[10px] uppercase tracking-wide text-red-700 bg-red-100 px-1.5 py-0.5 rounded">No school</span>}
                          {variant === 'early_release' && <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Early release</span>}
                          {time && <span className="text-xs text-gray-500">· {time}</span>}
                        </div>
                        <div className="text-xs text-gray-500 flex flex-wrap gap-x-2">
                          {source && <span>{source}</span>}
                          {e.affected_kids && e.affected_kids.length > 0 && <span>· {e.affected_kids.join(', ')}</span>}
                          {e.event_type && e.event_type !== 'school_event' && <span className="capitalize">· {e.event_type.replace(/_/g, ' ')}</span>}
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default UpcomingSchoolEvents
